/**
 * 🐱 **마을 친구의 역할과 단계** — NPC마다 「무엇을 하는 친구인지」와 「어디까지 말하는지」.
 *
 * ## 왜 「켜기/끄기」가 아니라 3단인가
 * 타깃이 **0~7세**다(기획 §4.3). 그 나이 아이는 글을 읽지도 타이핑을 하지도 못한다 —
 * 자유 채팅은 「위험해서 막는 것」이 아니라 **애초에 아이가 쓸 수 없는 물건**이고,
 * 실제로 말을 거는 건 옆에 있는 보호자·교사다.
 *
 * 그래서 가운데 칸(1단계)이 필요하다: **AI가 미리 만들고 운영자가 승인한 말.**
 * 아이 화면에서는 0단계와 똑같이 안전한데(정해진 답만 나온다) 내용은 훨씬 풍부해지고,
 * **실시간 AI가 필요 없다** — 실시간 서버가 꺼져 있어도, 인터넷이 끊겨도 동작한다.
 *
 * 이건 이 저장소가 이미 쓰는 방식이다(NPC 대사 승인제). 같은 구조를 그대로 쓴다.
 *
 * ## 단계
 * - `0` 버튼만 — 정해진 안내. **기본값**. AI가 끼지 않으므로 틀린 말을 할 수 없다
 * - `1` 승인된 문답 — AI 초안 → 운영자 ✅ → 아이에게. 실시간 호출 없음
 * - `2` 자유 대화 — 그때그때 AI가 답한다. **어른 확인 뒤에만**, 실시간 서버가 있을 때만
 */
// 확장자를 적는다 — 검사 스크립트가 node 로 이 파일을 직접 읽는다(mapStuck·heroOverlayFit 과 같은 이유)
import { isLang, type Lang } from "./ai.ts";

/** 아이·보호자 자유 대화에 연결할 공급자. 운영자 초안의 Grok 선택과는 분리한다. */
export const DOCENT_PROVIDERS = ["anthropic", "openai", "google"] as const;
export type DocentProvider = (typeof DOCENT_PROVIDERS)[number];
export function isDocentProvider(v: unknown): v is DocentProvider {
  return typeof v === "string" && (DOCENT_PROVIDERS as readonly string[]).includes(v);
}

/** NPC가 맡을 수 있는 역할. 화면 라벨·기본 안내문은 `NPC_ROLE_META`. */
export const NPC_ROLES = [
  "librarian",
  "heritage",
  "book_talk",
  "guide",
  "multilingual",
  "guardian",
  "rally",
] as const;
export type NpcRole = (typeof NPC_ROLES)[number];

/** 0=버튼만 · 1=승인된 문답 · 2=자유 대화. 숫자가 클수록 아이에게서 멀어진다. */
export type NpcLevel = 0 | 1 | 2;

export interface NpcRoleMeta {
  id: NpcRole;
  /** 관리자 화면에 그대로 나오는 이름. */
  label: string;
  /** 누가 말을 거는가 — 위험도가 여기서 갈린다. */
  who: "child" | "adult" | "both";
  /** 한 줄 설명(운영자가 고를 때 읽는다). */
  hint: string;
  /**
   * 이 역할이 **아이 앞에서 자유 대화를 해도 되는가**.
   * false 면 관리자에서 2단계를 못 고른다 — 고르게 해 두고 나중에 막으면
   * 운영자는 「분명 켰는데 안 된다」만 겪는다.
   */
  freeChatOk: boolean;
}

/**
 * 역할표 — **단일 정의**. 관리자 선택지도, 웹 표시도, 초안 프롬프트도 여기서 나온다.
 * 화면마다 다시 적으면 「관리자엔 있는데 화면엔 없는 역할」이 생긴다.
 */
export const NPC_ROLE_META: Readonly<Record<NpcRole, NpcRoleMeta>> = {
  librarian: {
    id: "librarian",
    label: "📚 책방 사서",
    who: "both",
    hint: "이용법·길찾기·자주 묻는 것을 안내해요",
    freeChatOk: false,
  },
  heritage: {
    id: "heritage",
    label: "🏛️ 유물 해설사",
    who: "both",
    hint: "첨성대·석굴암 같은 경주 유적을 아이 눈높이로 알려 줘요",
    freeChatOk: false,
  },
  book_talk: {
    id: "book_talk",
    label: "📖 책 이야기 나누기",
    who: "child",
    hint: "다 읽은 책으로 「왜 그랬을까?」를 물어봐요 (어른이 함께 볼 때)",
    freeChatOk: true,
  },
  guide: {
    id: "guide",
    label: "⭐ 길잡이",
    who: "child",
    hint: "별을 몇 개 모았는지, 어디를 아직 안 가봤는지 알려 줘요",
    freeChatOk: false,
  },
  multilingual: {
    id: "multilingual",
    label: "🌏 다국어 안내원",
    who: "adult",
    hint: "외국인 관광객·교사에게 같은 안내를 그 나라 말로",
    freeChatOk: true,
  },
  guardian: {
    id: "guardian",
    label: "👨‍👩‍👧 보호자 상담",
    who: "adult",
    hint: "몇 살에 맞는지, 어떻게 읽어 주면 좋은지 — 어른에게만",
    freeChatOk: true,
  },
  rally: {
    id: "rally",
    label: "🎁 랠리 도우미",
    who: "adult",
    hint: "현장에서 다음 지점·운영시간을 안내해요",
    freeChatOk: false,
  },
};

export function isNpcRole(v: unknown): v is NpcRole {
  return typeof v === "string" && (NPC_ROLES as readonly string[]).includes(v);
}

/** 이 역할에서 고를 수 있는 단계 — 자유 대화가 안 되는 역할은 2가 아예 안 나온다. */
export function allowedLevels(role: NpcRole): NpcLevel[] {
  return NPC_ROLE_META[role].freeChatOk ? [0, 1, 2] : [0, 1];
}

// ── 문답(1단계) ──────────────────────────────────────────────

/** 승인 상태 — NPC 대사와 같은 말을 쓴다(운영자가 두 화면에서 같은 뜻으로 읽게). */
export type QaStatus = "pending" | "approved" | "rejected";

export interface NpcQa {
  id: string;
  /** 아이·보호자가 누를 질문(버튼 글). */
  q: string;
  /** 그 질문에 나갈 답. */
  a: string;
  status: QaStatus;
  /** AI 초안인지 사람이 쓴 것인지 — 검수할 때 우선순위가 다르다. */
  source?: "ai" | "manual";
  /** 이 문답의 언어. 없으면 한국어(원문)로 본다 — 옛 저장본이 그렇다. */
  lang?: Lang;
  /**
   * 번역이면 **원문 문답의 id**. 관리자에서 원문 옆에 묶어 보여 주고,
   * 원문을 지울 때 번역도 같이 지울지 물어볼 수 있게 한다.
   */
  of?: string;
}

export interface NpcAgent {
  /** 마을 오브젝트의 NPC id(`npc.<id>` 의 뒷부분). */
  npc: string;
  role: NpcRole;
  level: NpcLevel;
  /** 자유 대화 공급자. 옛 저장본은 Claude로 유지한다. */
  provider: DocentProvider;
  /** 어떤 자료 묶음을 쓸지(AI 자료 화면의 묶음 id). */
  packs: string[];
  qa: NpcQa[];
}

export interface NpcAgentDoc {
  agents: NpcAgent[];
}

export const NPC_AGENT_LIMITS = {
  /** 한 NPC가 가질 수 있는 문답 수 — 버튼이 이보다 많으면 아이가 못 고른다. */
  qa: 40,
  /** 아이 화면에 한 번에 보여 줄 버튼 수. */
  shown: 6,
  q: 60,
  a: 400,
  packs: 12,
} as const;

const str = (v: unknown, max: number): string =>
  typeof v === "string" ? v.trim().slice(0, max) : "";

const ID_RE = /^[a-z0-9_-]{1,48}$/;

function normalizeQa(input: unknown, i: number): NpcQa | null {
  const o = (input ?? {}) as Record<string, unknown>;
  const q = str(o.q, NPC_AGENT_LIMITS.q);
  const a = str(o.a, NPC_AGENT_LIMITS.a);
  if (!q || !a) return null; // 한쪽만 있는 건 문답이 아니다
  const id = typeof o.id === "string" && ID_RE.test(o.id) ? o.id : `qa_${i}`;
  const status: QaStatus =
    o.status === "approved" || o.status === "rejected" ? o.status : "pending";
  return {
    id,
    q,
    a,
    status,
    ...(o.source === "ai" || o.source === "manual" ? { source: o.source } : {}),
    // 🔴 언어를 버리면 번역이 전부 한국어로 뭉친다 — 화면은 멀쩡히 뜨고 말만 섞인다.
    ...(isLang(o.lang) ? { lang: o.lang } : {}),
    ...(typeof o.of === "string" && ID_RE.test(o.of) ? { of: o.of } : {}),
  };
}

/**
 * 밖에서 온 문서를 믿을 수 있는 모양으로 — 게시본·관리자 저장본 둘 다 여기를 지난다.
 *
 * **모르는 값은 버리지 말고 기본값으로 떨어뜨린다**: 역할 이름이 하나 오타 나면
 * 그 NPC 설정 전체가 사라지는 게 아니라 「책방 사서 · 버튼만」으로 돌아간다.
 */
export function normalizeAgentDoc(input: unknown): NpcAgentDoc {
  const o = (input ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(o.agents) ? o.agents : [];
  const seen = new Set<string>();
  const agents: NpcAgent[] = [];
  for (const item of raw) {
    const a = (item ?? {}) as Record<string, unknown>;
    const npc = typeof a.npc === "string" && ID_RE.test(a.npc) ? a.npc : "";
    if (!npc || seen.has(npc)) continue; // 같은 NPC 가 둘이면 앞의 것만
    seen.add(npc);
    const role: NpcRole = isNpcRole(a.role) ? a.role : "librarian";
    const lvRaw = typeof a.level === "number" ? a.level : 0;
    const lv: NpcLevel = lvRaw === 1 || lvRaw === 2 ? lvRaw : 0;
    // 자유 대화가 안 되는 역할에 2가 저장돼 있으면 1로 내린다 — 저장은 됐어도 열어 주지 않는다
    const level: NpcLevel = allowedLevels(role).includes(lv) ? lv : 1;
    const packs = (Array.isArray(a.packs) ? a.packs : [])
      .filter((p): p is string => typeof p === "string" && ID_RE.test(p))
      .slice(0, NPC_AGENT_LIMITS.packs);
    const qa = (Array.isArray(a.qa) ? a.qa : [])
      .map((x, i) => normalizeQa(x, i))
      .filter((x): x is NpcQa => x !== null)
      .slice(0, NPC_AGENT_LIMITS.qa);
    const provider = isDocentProvider(a.provider) ? a.provider : "anthropic";
    agents.push({ npc, role, level, provider, packs, qa });
  }
  return { agents };
}

/** 이 NPC 의 설정(없으면 null — 화면은 예전처럼 버튼식으로 간다). */
export function agentFor(doc: NpcAgentDoc, npc: string): NpcAgent | null {
  return doc.agents.find((a) => a.npc === npc) ?? null;
}

/**
 * 아이 화면으로 나갈 것만 — **승인된 문답, 1단계 이상**.
 *
 * 대기·거절은 절대 나가면 안 된다(승인제의 전부다). 0단계 NPC 는 문답이 쌓여 있어도
 * 안 내보낸다 — 운영자가 「아직 안 켰다」고 한 것이다.
 */
export function publicAgents(doc: NpcAgentDoc): NpcAgent[] {
  return doc.agents
    .filter((a) => a.level >= 1)
    .map((a) => ({ ...a, qa: a.qa.filter((q) => q.status === "approved") }))
    .filter((a) => a.qa.length > 0 || a.level === 2);
}

/** 이 문답이 어느 말인가 — 안 적혀 있으면 한국어(원문)다. */
export const qaLang = (q: NpcQa): Lang => q.lang ?? "ko";

/**
 * 이 언어로 보여 줄 문답 — **승인된 것만**, 없으면 한국어로 떨어진다.
 * 개수 제한도 여기서 한다(버튼이 많으면 아이가 못 고른다).
 *
 * 번역도 **따로 승인**해야 한다. 원문을 ✅ 했다고 번역이 자동으로 나가면,
 * 검수한 적 없는 문장이 아이에게 가는 셈이다 — 승인제의 요점이 그것이다.
 */
export function qaFor(agent: NpcAgent, lang: Lang = "ko", limit = NPC_AGENT_LIMITS.shown): NpcQa[] {
  const approved = agent.qa.filter((q) => q.status === "approved");
  const wanted = approved.filter((q) => qaLang(q) === lang);
  const use = wanted.length > 0 ? wanted : approved.filter((q) => qaLang(q) === "ko");
  return use.slice(0, limit);
}

/** 이 친구가 답할 수 있는 말들 — 골라 주는 칸을 언제 띄울지 정한다(하나뿐이면 안 띄운다). */
export function qaLangs(agent: NpcAgent): Lang[] {
  const set = new Set<Lang>();
  for (const q of agent.qa) if (q.status === "approved") set.add(qaLang(q));
  return (["ko", "en", "ja", "zh"] as Lang[]).filter((l) => set.has(l));
}

/** 검수 대기 수 — 관리자 배지·출시 준비에 쓴다. */
export function pendingCount(doc: NpcAgentDoc): number {
  return doc.agents.reduce((n, a) => n + a.qa.filter((q) => q.status === "pending").length, 0);
}

/**
 * 역할 → 도슨트 설정(`NpcConfig`). **자유 대화의 답변 범위를 정한다.**
 *
 * 범위를 역할에서 끌어오는 이유: 유물 해설사에게 굿즈를 물으면 「그건 제가 몰라요」가 나와야
 * 한다. 하나의 범위를 모든 친구가 공유하면 어느 친구에게 물어도 같은 답이 나오고,
 * 그러면 친구를 나눈 의미가 없다.
 *
 * 버튼은 **승인된 문답**을 그대로 쓴다 — 자유 대화가 막혔을 때(상한 초과·모델 실패)
 * 떨어질 자리가 그것이다(불변식 3).
 */
export function docentConfigForRole(role: NpcRole, qa: readonly NpcQa[] = []): {
  scope: string;
  outOfScope: string;
  buttons: { id: string; label: string; answer: string }[];
} {
  return {
    scope: ROLE_SCOPE[role],
    outOfScope:
      "개인정보, 의료 조언, 무섭거나 폭력적인 내용은 도와드리기 어려워요. 다른 이야기는 함께해요!",
    buttons: qa
      .filter((q) => q.status === "approved")
      .slice(0, NPC_AGENT_LIMITS.shown)
      .map((q) => ({ id: q.id, label: q.q, answer: q.a })),
  };
}

/** 역할마다 답할 수 있는 범위 — 모델 시스템 프롬프트에 그대로 들어간다. */
const ROLE_SCOPE: Readonly<Record<NpcRole, string>> = {
  librarian: "이 서비스 이용 방법(전자책·체험공간·AR·굿즈 화면을 어떻게 보는지)과 자주 묻는 것",
  heritage: "경주 문화유산(첨성대·석굴암·천마총·동궁과 월지·수막새)과 그 이야기",
  book_talk:
    "그림책의 줄거리·등장인물·느낌, 그레그·병아리·마을 친구들, 선택한 참고 자료에 담긴 경주 이야기와 마을 이용 방법",
  guide: "이 마을에서 무엇을 해 볼 수 있는지(별 모으기·카드·포털)",
  multilingual: "이 서비스와 경주 문화유산 안내(질문한 언어로)",
  guardian: "이 책과 서비스를 아이와 어떻게 함께 보면 좋은지, 몇 살에 맞는지",
  rally: "스탬프 랠리 지점·운영시간·참여 방법",
};
